import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Box, Slider, Typography } from '@mui/material'
import { isArray, minutesToLabel, labelToMinutes } from './utils'

export const CommonSlider = ({
  id = 'common-distance-slider',
  variant = 'distance',
  isRange = false,
  value,
  onChange,
  min,
  max,
  step,
  title,
}) => {
  const config = useMemo(() => {
    if (variant === 'time') {
      const computedMin = min ?? 0
      const computedMax = max ?? 23 * 60 + 59
      const computedStep = step ?? 30
      return {
        min: computedMin,
        max: computedMax,
        step: computedStep,
        aria: 'common-times-slider',
      }
    }
    const computedMin = min ?? 1
    const computedMax = max ?? 51
    const computedStep = step ?? 1
    return {
      min: computedMin,
      max: computedMax,
      step: computedStep,
      aria: 'common-distance-slider',
    }
  }, [variant, min, max, step])

  const formatDisplayValue = useCallback(
    v => {
      if (variant === 'time') return minutesToLabel(v)
      return v === config.max ? 'Any' : `${v} mi`
    },
    [variant, config.max]
  )

  const mapIn = useCallback(
    v => {
      if (variant === 'time')
        return typeof v === 'string' ? labelToMinutes(v) : v
      return v === 'any' ? config.max : v
    },
    [variant, config.max]
  )

  const mapOut = useCallback(
    v => {
      if (variant === 'time') return minutesToLabel(v)
      return v === config.max ? 'any' : v
    },
    [variant, config.max]
  )

  const sliderValue = useMemo(() => {
    if (value === undefined || value === null)
      return isRange ? [config.min, config.max] : config.min

    if (isArray(value)) {
      const mapped = value.map(mapIn)
      if (variant === 'time' && isRange) {
        const endLabel = value[1]
        if (
          typeof endLabel === 'string' &&
          endLabel.trim().toUpperCase() === '00:00 AM'
        ) {
          mapped[1] = config.max
        }
      }
      return mapped
    }
    return mapIn(value)
  }, [value, isRange, config.min, config.max, mapIn])

  const handleChange = useCallback(
    (_, newValue) => {
      if (!onChange) return

      const payload = isArray(newValue)
        ? newValue.map(mapOut)
        : mapOut(newValue)

      onChange(payload)
    },
    [onChange, mapOut]
  )

  return (
    <Box id={id} data-testid={id} sx={{ width: '100%' }}>
      <Typography
        sx={{
          mb: { xs: 0, md: 1 },
          fontWeight: 400,
          typography: { xs: 'body2', md: 'body1' },
        }}
      >
        {(() => {
          if (title) return title
          if (variant === 'time') return 'Select a Time Range:'
          return 'How far are you willing to travel? '
        })()}
      </Typography>
      <Slider
        min={config.min}
        max={config.max}
        step={config.step}
        value={sliderValue}
        onChange={handleChange}
        valueLabelDisplay='on'
        getAriaLabel={() => config.aria}
        valueLabelFormat={v => formatDisplayValue(v)}
        getAriaValueText={v => formatDisplayValue(v)}
        sx={{
          color: theme => theme.palette.primary.main,
          height: 10,
          '& .MuiSlider-track': { border: 'none' },
          '& .MuiSlider-rail': {
            opacity: 1,
            backgroundColor: 'secondary.main',
          },
          '& .MuiSlider-thumb': {
            height: 26,
            width: 26,
            backgroundColor: 'primary.main',
            '&:focus, &:hover, &.Mui-active': { boxShadow: 'none' },
          },
          '& .MuiSlider-valueLabel': {
            lineHeight: 1.2,
            fontSize: { xs: 12, md: 16 },
            background: 'white',
            color: 'text.primary',
            padding: 0,
            top: 50,
            '&:before': { display: 'none' },
          },
        }}
      />
    </Box>
  )
}

CommonSlider.propTypes = {
  id: PropTypes.string,
  variant: PropTypes.oneOf(['distance', 'time']),
  isRange: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    ),
  ]),
  onChange: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  step: PropTypes.number,
  title: PropTypes.node,
}
